import { LimitFeature } from "app/types";
import { fetchAuthToken } from "app/Auth";
import { withLimitHit } from "app/analytics";
import Constants from "common/constant";
import { client } from "common/apiClient";

export interface UsageInfo {
  used: number;
  limit: number;
  remaining: number;
}

export interface SSECallbacks {
  onMessageStart: (messageId: string, sessionId: string) => void;
  onContentDelta: (delta: string) => void;
  onMessageEnd: (messageId: string, usage?: UsageInfo) => void;
  onError: (code: string, message: string) => void;
  onSessionReset?: (newSessionId: string) => void;
}

const DEFAULT_SVG_SIZE = 512;

const canRasterizeSvg = () => typeof window !== "undefined" && typeof document !== "undefined";

const isSvgUrl = (url: string) => {
  if (!url) return false;
  if (url.startsWith("data:image/svg+xml")) return true;
  return /\.svg(?:\?.*)?$/i.test(url);
};

const parseSvgLength = (svgText: string, attr: "width" | "height") => {
  const match = svgText.match(new RegExp(`${attr}="([^"]+)"`, "i"));
  if (!match) return undefined;
  const raw = match[1].trim();
  if (!raw || raw.endsWith("%")) return undefined;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : undefined;
};

const parseSvgViewBox = (svgText: string) => {
  const match = svgText.match(/viewBox="([^"]+)"/i);
  if (!match) return undefined;
  const parts = match[1]
    .trim()
    .split(/[\s,]+/)
    .map((part) => Number.parseFloat(part))
    .filter((value) => Number.isFinite(value));
  if (parts.length !== 4) return undefined;
  const width = parts[2];
  const height = parts[3];
  if (width <= 0 || height <= 0) return undefined;
  return { width, height };
};

const getSvgDimensions = (svgText: string) => {
  const widthAttr = parseSvgLength(svgText, "width");
  const heightAttr = parseSvgLength(svgText, "height");
  const viewBox = parseSvgViewBox(svgText);

  let width = widthAttr ?? viewBox?.width;
  let height = heightAttr ?? viewBox?.height;

  if (width && !height && viewBox) {
    height = Math.round((width * viewBox.height) / viewBox.width);
  } else if (height && !width && viewBox) {
    width = Math.round((height * viewBox.width) / viewBox.height);
  }

  return { width, height };
};

const loadSvgImage = (svgText: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    img.src = url;
  });

const svgTextToPngDataUrl = async (svgText: string): Promise<string> => {
  const { width, height } = getSvgDimensions(svgText);
  const img = await loadSvgImage(svgText);

  const targetWidth = Math.max(1, Math.round(width ?? img.naturalWidth ?? DEFAULT_SVG_SIZE));
  const targetHeight = Math.max(1, Math.round(height ?? img.naturalHeight ?? DEFAULT_SVG_SIZE));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2d context is not available");
  }
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  return canvas.toDataURL("image/png");
};

const decodeSvgDataUrl = (dataUrl: string) => {
  const parts = dataUrl.split(",");
  if (parts.length < 2) return "";
  const header = parts[0];
  const data = parts.slice(1).join(",");
  if (header.includes(";base64")) {
    return atob(data);
  }
  return decodeURIComponent(data);
};

const fetchSvgText = async (url: string, signal?: AbortSignal) => {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch svg: ${response.status}`);
  }
  return response.text();
};

const resolveImageUrls = async (imageUrls?: string[], signal?: AbortSignal) => {
  if (!imageUrls || imageUrls.length === 0) return imageUrls;
  if (!canRasterizeSvg()) return imageUrls;

  const resolved = await Promise.all(
    imageUrls.map(async (url) => {
      if (!isSvgUrl(url)) return url;
      try {
        const svgText = url.startsWith("data:image/svg+xml")
          ? decodeSvgDataUrl(url)
          : await fetchSvgText(url, signal);
        if (!svgText) return url;
        return await svgTextToPngDataUrl(svgText);
      } catch (error) {
        console.warn("Failed to convert svg to png for ai chat:", error);
        return url;
      }
    }),
  );

  return resolved;
};

/**
 * メッセージ送信（SSE）
 */
export const sendMessageSSE = async ({
  uid,
  sessionId,
  message,
  imageUrls,
  pageUrl,
  callbacks: rawCallbacks,
  signal,
}: {
  uid: string;
  sessionId: string;
  message: string;
  imageUrls?: string[];
  pageUrl?: string;
  callbacks: SSECallbacks;
  signal?: AbortSignal;
}): Promise<void> => {
  // 上限は HTTP の 403（ACCESS_DENIED）か、SSE の error（PLAN_NOT_ALLOWED）で届く
  const callbacks = withLimitHit(rawCallbacks, LimitFeature.AI_CHAT, [
    "ACCESS_DENIED",
    "PLAN_NOT_ALLOWED",
    "QUOTA_EXCEEDED",
  ]);
  const token = await fetchAuthToken();
  const url = `${Constants.apiHost}/v1/ai/chat/${uid}/sessions/${sessionId}/messages`;
  const resolvedImageUrls = await resolveImageUrls(imageUrls, signal);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ message, image_urls: resolvedImageUrls, page_url: pageUrl }),
    signal,
  });

  if (!response.ok) {
    // 403エラーの場合、レスポンスボディからエラーコードを抽出
    if (response.status === 403) {
      try {
        const body = await response.json();
        callbacks.onError(
          body.code || "ACCESS_DENIED",
          body.message || "この機能はご利用いただけません",
        );
      } catch {
        callbacks.onError("ACCESS_DENIED", "この機能はご利用いただけません");
      }
      return;
    }
    callbacks.onError("HTTP_ERROR", `HTTP error: ${response.status}`);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError("NO_READER", "Response body is not readable");
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      let currentEvent = "";
      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7);
        } else if (line.startsWith("data: ")) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            handleSSEEvent(currentEvent, parsed, callbacks);
          } catch {
            // JSON parse error, skip
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};

const handleSSEEvent = (event: string, data: Record<string, unknown>, callbacks: SSECallbacks) => {
  switch (event) {
    case "message_start":
      callbacks.onMessageStart(data.message_id as string, data.session_id as string);
      break;
    case "content_delta":
      callbacks.onContentDelta(data.delta as string);
      break;
    case "message_end":
      callbacks.onMessageEnd(data.message_id as string, data.usage as UsageInfo | undefined);
      break;
    case "error":
      callbacks.onError(data.code as string, data.message as string);
      break;
    case "session_reset":
      callbacks.onSessionReset?.(data.new_session_id as string);
      break;
  }
};

// 履歴取得用の型定義
export interface HistorySession {
  id: string;
  title: string | null;
  messages: Array<{
    id: string;
    role: "user" | "assistant";
    content: string;
    image_urls: string[] | null;
    created_at: string;
  }>;
  created_at: string;
}

interface GetHistoryResponse {
  sessions: HistorySession[];
  has_more: boolean;
  next_cursor: string | null;
}

/**
 * 履歴取得（セッション + メッセージ一括取得、ページング対応）
 */
export const getAIChatHistory = async ({
  uid,
  limit = 10,
  beforeSessionId,
  excludeSessionId,
}: {
  uid: string;
  limit?: number;
  beforeSessionId?: string;
  excludeSessionId?: string;
}): Promise<GetHistoryResponse> => {
  const token = await fetchAuthToken();
  const params = new URLSearchParams({ limit: String(limit) });
  if (beforeSessionId) {
    params.append("before_session_id", beforeSessionId);
  }
  if (excludeSessionId) {
    params.append("exclude_session_id", excludeSessionId);
  }
  const response = await client({ token }).get<GetHistoryResponse>(
    `/v1/ai/chat/${uid}/history?${params.toString()}`,
  );
  return response.data;
};
